import * as React from 'react';
import {connect} from 'react-redux';
import {DisplayFeature, DisplayFeatureType, SpecialAttribute, UserSession} from '../domain';
import {
    deSelectFeatures,
    enableEditFeatureGeometry, nextPage, NextPage,
    onDeleteComments,
    OnDeleteFeature,
    onDeleteFeature,
    onNewFeatureComment,
    onResolveIssue, previousPage, PreviousPage,
    selectFeatures,
    SetFeatureSearchString,
    setFeatureSearchString, setPage, SetPage, setPageSize, SetPageSize
} from '../../../actions/actions';
import * as selectors from '../selectors';

import {SortDirection} from './editor/HeaderCellFormater';
import FeaturesState, {GridCoordinates, QualityParam, SortBy} from '../domain/FeaturesState';
import {
    Navigate,
    navigate,
    NavigateDirection,
    NavigateTo,
    navigateTo,
    ShowEditor,
    showEditor,
    SortFeatures,
    sortFeatures
} from '../reducers/actions';
import AttributeCell from './AttributeCell';
import OperationsCell from './cellComponents/OperationsCell';
import ReadOnlyCell from './cellComponents/ReadOnlyCell';
import SidePositionCell from './cellComponents/SidePositionCell';
import LaneCell from './cellComponents/LaneCell';
import TitleCell from './cellComponents/TitleCell';
import SortingHeader from './cellComponents/SortingHeader';
import FeatureTypeComment from './FeatureTypeComment';
import ToolsMenu from './ToolsMenu';
import ViewSettingsMenu from './ViewSettingsMenu';
import {FeatureTypeLock} from './FeatureTypeLock';
import {AttributeType} from '../../../../integration/datacatalog/datacatalog-services';
import classNames = require('classnames');
import CopyGeometryModal from './CopyGeometry/CopyGeometryModal';
import {
    allSidePositionsEqualRelativeToMeteringDirection,
    getAllSidePositionsAsTextRelativeToMeteringDirection
} from '../../../../sidePositionUtilities';
import {Button, Modal, ModalBody, ModalFooter, ModalHeader} from 'react-bootstrap';
import CommentsModal from './CommentsModal/CommentsModal';
import {
    getAllLanesAsTextRelativeToMeteringDirection,
    lanesEqualForAllLocationalsRelativeToMeteringDirection
} from '../../../../laneUtilities';
import {allLocationalsHaveMeteringDirection} from '../../../../meteringDirectionUtilities';
import {OrderedComments} from '../selectors';
import MultipleFeatureSelector from './MultipleFeatureSelector';
import {Spinner} from '../../../shared-components/status/Spinner';
import HeightReferenceCell from './cellComponents/HeightReferenceCell';


interface StateProps {
    attributeTypes: AttributeType[];
    features: DisplayFeature[];
    gridFocus: GridCoordinates;
    showSubmittedFeatures: boolean;
    editorShown: boolean;
    featureSearchString?: string;
    numberOfNonAttributeColumns: number;
    specialAttributes: SpecialAttribute[];
    needToNavigate: GridCoordinates;
    qualityParams: QualityParam[];
    isFetching: boolean;
    selectedFeatures: string[];
    user: UserSession;
    contractId: string;
    editingGeometryFeatureId: string;
    selectedFeatureType: DisplayFeatureType;
    roadSystemReferences: Record<string, string>;
    comments: OrderedComments;
    loadingAttributes: Record<string, Record<number, boolean>>;
    loadingSpecialAttributes: Record<string, Record<SpecialAttribute, boolean>>;
    page: number;
    pageSize: number;
}

interface DispatchProps {
    doNavigate: Navigate;
    doNavigateTo: NavigateTo;
    doShowEditor: ShowEditor;
    deSelectFeatures: Function;
    selectFeatures: Function;
    onDeleteFeature: OnDeleteFeature;
    onResolveIssue: Function;
    onDeleteComments: Function;
    enableEditFeatureGeometry: Function;
    onNewComment: Function;
    doFeatureSearch?: SetFeatureSearchString;
    doSortFeatures: SortFeatures;
    setPageSize: SetPageSize;
    setPage: SetPage;
    previousPage: PreviousPage;
    nextPage: NextPage;
}

interface TagProps {
    activeTab: string;
}

type FeaturesTableProps = TagProps & StateProps & DispatchProps;

interface FeaturesTableState {
    onlyShowSelected?: boolean;
    showQualityParams?: boolean;
    showAllParams?: boolean;
    showAllFeatures?: boolean;
    columns?: any[];
    featureRows?: any[];
    originalFeatureRows?: any[];
    sortColumn?: string;
    sortDirection?: SortDirection;

    shouldShowToolsMenu: boolean;
    shouldShowViewSettingsMenu: boolean;
    autoScrollIntoView: boolean;

    // Press shift to select multiple rows
    selectMultiple: boolean;

    showMultipleRoadReferences: string[];

    timer: NodeJS.Timeout;
    localSearchString: string;
}

const mapStateToProps = (state: FeaturesState): StateProps => {
    return {
        loadingAttributes: selectors.loadingAttributes(state),
        loadingSpecialAttributes: selectors.loadingSpecialAttributes(state),
        attributeTypes: selectors.attributeTypes(state),
        features: selectors.features(state),
        gridFocus: selectors.gridFocus(state),
        showSubmittedFeatures: selectors.showSubmittedFeatures(state),
        selectedFeatureType: selectors.selectedFeatureType(state),
        featureSearchString: selectors.featureSearchString(state),
        numberOfNonAttributeColumns: selectors.numberOfNonAttributeColumns(state),
        specialAttributes: selectors.specialAttributes(state),
        needToNavigate: selectors.needToNavigate(state),
        qualityParams: selectors.qualityParams(state),
        editorShown: selectors.showEditor(state),
        isFetching: selectors.stateIsFetching(state),
        comments: selectors.availableComments(state),

        selectedFeatures: state.selectedFeatures,
        user: state.user,
        contractId: state.contractId,
        editingGeometryFeatureId: state.editingGeometryFeatureId,

        roadSystemReferences: selectors.roadSystemReferences(state),
        page: state.featuresPage,
        pageSize: state.featuresPageSize
    };
};

const mapFeaturesTableDispatchToProps: DispatchProps = ({
    doNavigate: navigate,
    doNavigateTo: navigateTo,
    doShowEditor: showEditor,
    doFeatureSearch: setFeatureSearchString,
    doSortFeatures: sortFeatures,
    onDeleteFeature,
    onResolveIssue,
    onDeleteComments,
    enableEditFeatureGeometry,
    onNewComment: onNewFeatureComment,
    deSelectFeatures,
    selectFeatures,
    setPageSize,
    setPage,
    previousPage,
    nextPage
});

const MAX_PAGE_SIZE = 5000;

const validPageSizes = [25, 50, 100, 250, MAX_PAGE_SIZE];


class FeaturesTable extends React.Component<FeaturesTableProps, FeaturesTableState> {

    private tableHead: HTMLTableSectionElement;
    private gridElement: HTMLDivElement;
    private containerElement: HTMLDivElement;

    constructor(props: FeaturesTableProps) {
        super(props);
        this.state = {
            shouldShowToolsMenu: false,
            shouldShowViewSettingsMenu: false,
            autoScrollIntoView: false,
            selectMultiple: false,
            showMultipleRoadReferences: [],
            timer: null,
            localSearchString: props.featureSearchString
        };
        this.handleTableKeyPress = this.handleTableKeyPress.bind(this);
        this.handleTableKeyUp = this.handleTableKeyUp.bind(this);
        this.handleGridClick = this.handleGridClick.bind(this);
        this.handleSearchInputWithDebounce = this.handleSearchInputWithDebounce.bind(this);
        this.handleSetPage = this.handleSetPage.bind(this);
    }

    componentWillReceiveProps(nextProps: Readonly<FeaturesTableProps>) {
        if (nextProps.needToNavigate) {
            this.props.doNavigateTo(nextProps.needToNavigate);
        }
    }

    componentWillMount() {

        if (localStorage.getItem('pageSize') === null) {
            localStorage.setItem('pageSize', '100');
        }

        // adding key listener for keyboard navigation
        document.addEventListener('keydown', this.handleTableKeyPress);
        document.addEventListener('keyup', this.handleTableKeyUp);
    }

    componentDidMount() {
        const existing = localStorage.getItem('pageSize');
        if (existing) {
            this.props.setPageSize(Number.parseInt(existing, 10));
        }
    }

    componentDidUpdate(prevProps: Readonly<FeaturesTableProps>, prevState: Readonly<FeaturesTableState>, prevContext: any): void {
        if (JSON.stringify(prevProps.gridFocus) !== JSON.stringify(this.props.gridFocus)) {
            const focusCell = this.gridElement.querySelector('.cell-focus');
            if (this.state.autoScrollIntoView) {
                focusCell && focusCell.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
                this.setState({autoScrollIntoView: false});
            }
        }

        this.onScroll();
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleTableKeyPress);
        document.removeEventListener('keyup', this.handleTableKeyUp);
    }

    setTableHeadRef = (element: HTMLTableSectionElement) => {
        this.tableHead = element;
    }

    addGridRef = (element: HTMLDivElement) => {
        this.gridElement = element;
    }

    onScroll() {
        // Sticky titles
        const translateX = 'translate(' + this.gridElement.scrollLeft + 'px, 0)';
        const gridTitles = this.gridElement.querySelectorAll('.sticky');
        gridTitles.forEach(elm => (elm as HTMLElement).style.transform = translateX);

        // Sticky headers
        const translateY = 'translate(0,' + this.gridElement.scrollTop + 'px)';
        this.tableHead.style.transform = translateY;
    }

    toggleFeature(feature: DisplayFeature) {
        if (this.state.selectMultiple) {
            const features = this.props.features;
            const selectedFeatures = this.props.selectedFeatures;
            const previousFeature = features.find(obj => obj.id === selectedFeatures.slice(-1)[0]);

            const indexOfCurrent = features.indexOf(feature);
            const indexOfPrevious = features.indexOf(previousFeature);

            const toBeToggled = (indexOfCurrent > indexOfPrevious ?
                features.slice(indexOfPrevious + 1, indexOfCurrent + 1) :
                features.slice(indexOfCurrent, indexOfPrevious));

            toBeToggled.forEach(feature => {
                if (!feature.isSelected) {
                    this.props.selectFeatures(feature.id);
                }
            });

            // Prevents text in selected rows from being highlighted when shift-clicking
            document.getSelection().removeAllRanges();

        } else {
            if (feature.isSelected) {
                this.props.deSelectFeatures(feature.id);
            } else {
                this.props.selectFeatures(feature.id);
            }
        }
    }

    handleTableKeyPress(e: KeyboardEvent) {

        if (this.props.editorShown) return; // Editor should handle keypresses

        let navigateTo = null;

        switch (e.key) {
            case 'ArrowLeft':
                navigateTo = NavigateDirection.LEFT;
                break;
            case 'ArrowRight':
                navigateTo = NavigateDirection.RIGHT;
                break;
            case 'ArrowUp':
                navigateTo = NavigateDirection.UP;
                break;
            case 'ArrowDown':
                navigateTo = NavigateDirection.DOWN;
                break;
            case 'Enter':
                this.handleEnterPress();
                break;
            case 'Shift':
                this.handleShiftPress();
                break;
        }

        if (navigateTo !== null) {
            const inputElementPreventsNavigation = !!this.containerElement.querySelector('.preventGridNavigation:focus');
            if (!inputElementPreventsNavigation) {
                e.preventDefault();
                this.setState({autoScrollIntoView: true});
                this.props.doNavigate(navigateTo);
            }
        }
    }

    handleTableKeyUp(e: KeyboardEvent) {
        switch (e.key) {
            case 'Shift':
                this.handleShiftRelease();
                break;
        }
    }

    handleEnterPress() {
        if (this.props.isFetching) {
            return;     // stops edit actions while another is in progress
        }
        const focusedCell = this.gridElement.querySelector('.cell-focus');
        if (!focusedCell) {
            return; // untruthy value means no focused cell is present to operate on
        }
        const isEditable = !!focusedCell.closest('.editable');
        const isLocked = !!focusedCell.closest('.locked');
        const isRowDisabled = !!focusedCell.closest('.disabled');
        const isCellDisabled = !!focusedCell.querySelector('.disabled');

        if (isEditable && !isLocked && !isRowDisabled && !isCellDisabled) {
            this.props.doShowEditor();
        }
    }

    handleShiftPress() {
        this.setState({selectMultiple: true});
    }

    handleShiftRelease() {
        this.setState({selectMultiple: false});
    }

    handleGridClick(e: React.MouseEvent<HTMLDivElement>) {
        // Get the nearest gridcell ancestor
        const cell = (e.target as HTMLDivElement).closest('td.gridcell');

        if (cell) {
            const isEditable = cell.classList.contains('editable');
            const hasFocus = !!cell.querySelector('.cell-focus');
            const isUnlocked = !cell.closest('.locked');
            const isRowDisabled = !!cell.closest('.disabled');
            const isCellDisabled = !!cell.querySelector('.disabled');

            const gridCoord = cell.getAttribute('data-gridcoord');
            const [x, y] = gridCoord.split(',');

            // Check if the cell is already selected. If so, edit cell
            if (hasFocus && isEditable && isUnlocked && !isRowDisabled && !isCellDisabled) {
                this.props.doShowEditor();
            } else {
                this.props.doNavigateTo(new GridCoordinates(Number(x), Number(y)));
            }
        }

        // close any menu if clicked outside.
        const toolsMenuElement = (e.target as HTMLDivElement).closest('#toolsMenuBoundry');
        if (!toolsMenuElement) this.setState({shouldShowToolsMenu: false});

        const ViewSettingsMenuElement = (e.target as HTMLDivElement).closest('#ViewSettingsMenuBoundry');
        if (!ViewSettingsMenuElement) this.setState({shouldShowViewSettingsMenu: false});
    }

    handleSearchInput(searchString: string) {
        this.props.doFeatureSearch(searchString);
    }

    handleSearchInputWithDebounce(value: string) {
        clearTimeout(this.state.timer);
        this.setState(({
            localSearchString: value,
            timer: setTimeout(() => this.handleSearchInput(value), 500)
        }));
    }

    toggleShowToolsMenu() {
        this.setState(
            {shouldShowToolsMenu: !this.state.shouldShowToolsMenu}
        );
    }

    toggleShowViewSettingsMenu() {
        this.setState(
            {shouldShowViewSettingsMenu: !this.state.shouldShowViewSettingsMenu}
        );

    }

    getDatakatalogUrl() {
        const {selectedFeatureType} = this.props;
        return `https://datakatalogen.atlas.vegvesen.no/#/${selectedFeatureType.typeId}`;
    }

    getQualityParamValue(feature: DisplayFeature, qualityParam: QualityParam) {
        const qualityParamType = qualityParam.key;
        const value = (feature.geometry as any)[qualityParamType];
        if (value == null) {
            return null;
        } else if (qualityParamType === 'datafangstdato') {
            return this.toNorwegianDateFormat(value);
        } else {
            return value;
        }
    }

    toNorwegianDateFormat(date: string) {
        const year = date.substring(0, 4);
        const month = date.substring(5, 7);
        const day = date.substring(8, 10);

        return day + '.' + month + '.' + year;
    }

    handleSetPage(page: number) {
        return () => {
            this.props.setPage(page);
        };
    }

    handleSetPageSize(size: number) {
        localStorage.setItem('pageSize', size.toString(10));

        this.props.setPageSize(size);
    }

    render() {
        const {attributeTypes, features, gridFocus, selectedFeatureType, qualityParams, specialAttributes, loadingAttributes, page, pageSize} = this.props;

        const pages = Math.ceil(selectedFeatureType.totalWithCriteria / pageSize) - 1;

        const showHeightRef = attributeTypes.some((attributeType) => (attributeType.heightReference > 0));

        return (

            <div id="df-datagrid-container" onClick={(e) => this.handleGridClick(e)}
                 ref={(elm) => this.containerElement = elm}>
                <Modal show={this.state.showMultipleRoadReferences.length > 0} onHide={() => this.setState({showMultipleRoadReferences: []})}>
                    <ModalHeader>Vegsystemreferanser</ModalHeader>
                    <ModalBody>
                        <ul>
                            {
                                this.state.showMultipleRoadReferences.map(ref => <li key={ref}>{ref}</li>)
                            }
                        </ul>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={() => this.setState({showMultipleRoadReferences: []})}>Lukk</Button>
                    </ModalFooter>
                </Modal>
                <div id="df-datagrid-header">
                    <div className="header-item">
                        <FeatureTypeLock />
                        <MultipleFeatureSelector />
                        <h2>
                            <a href={this.getDatakatalogUrl()}
                               target="_blank">{selectedFeatureType.name}
                            </a>
                            <div className="icon-left">
                                <FeatureTypeComment/>
                            </div>
                        </h2>
                    </div>
                    <div className="header-item search-panel">
                        Søk <input type="text"
                                   value={this.state.localSearchString}
                                   className="preventGridNavigation"
                                   onChange={(e) => this.handleSearchInputWithDebounce(e.target.value)} placeholder="kurve-3, nvdbid, fritekst"/>

                    </div>
                    <div className="header-item paging-panel">
                        {
                            this.props.selectedFeatureType.totalWithCriteria > 25 && <span className={'page-size-selector'}>
                            <select value={pageSize} onChange={(e) => {
                                const size = Number.parseInt(e.target.value, 10);
                                this.handleSetPageSize(size);
                            }}>
                                {validPageSizes.map(value => <option key={value} value={value}>{value === MAX_PAGE_SIZE
                                    ? this.props.selectedFeatureType.totalWithCriteria > MAX_PAGE_SIZE ? MAX_PAGE_SIZE : `Alle (${this.props.selectedFeatureType.totalWithCriteria})`
                                    : value}</option>)}
                            </select>
                            <p>per side</p>
                        </span>
                        }
                        <span className={'page-selector'}>
                            <p>{Math.min(this.props.selectedFeatureType.totalWithCriteria, this.props.pageSize * this.props.page + 1)}-{Math.min(this.props.selectedFeatureType.totalWithCriteria, this.props.pageSize * (this.props.page + 1))} av {this.props.selectedFeatureType.totalWithCriteria}</p>
                            {
                                pages < 1 ? null : <>
                                    <Button disabled={page < 1} onClick={this.props.previousPage}><div className={'fas fa-angle-left'}/></Button>
                                    {pages <= 5
                                        ? (
                                            <>
                                                <Button disabled={page === 0} onClick={this.handleSetPage(0)}>1</Button>
                                                <Button disabled={page === 1} onClick={this.handleSetPage(1)}>2</Button>
                                                {pages >= 2 && <Button disabled={page === 2} onClick={this.handleSetPage(2)}>3</Button>}
                                                {pages >= 3 && <Button disabled={page === 3} onClick={this.handleSetPage(3)}>4</Button>}
                                                {pages >= 4 && <Button disabled={page === 4} onClick={this.handleSetPage(4)}>5</Button>}
                                            </>
                                        )
                                        : page < 3
                                            ? (
                                                <>
                                                    <Button onClick={this.handleSetPage(0)} disabled={page === 0}>1</Button>
                                                    <Button onClick={this.handleSetPage(1)} disabled={page === 1}>2</Button>
                                                    <Button onClick={this.handleSetPage(2)} disabled={page === 2}>3</Button>
                                                    <Button onClick={this.handleSetPage(3)}>4</Button>
                                                    <p>...</p>
                                                    <Button onClick={this.handleSetPage(pages)}>{pages}</Button>
                                                </>
                                            )
                                            : page + 3 > pages
                                                ? (
                                                    <>
                                                        <Button onClick={this.handleSetPage(0)}>1</Button>
                                                        <p>...</p>
                                                        <Button onClick={this.handleSetPage(pages - 3)}>{pages - 2}</Button>
                                                        <Button onClick={this.handleSetPage(pages - 2)} disabled={page === pages - 2}>{pages - 1}</Button>
                                                        <Button onClick={this.handleSetPage(pages - 1)} disabled={page === pages - 1}>{pages}</Button>
                                                        <Button onClick={this.handleSetPage(pages)} disabled={page === pages}>{pages + 1}</Button>
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        <Button onClick={this.handleSetPage(0)}>1</Button>
                                                        <p>...</p>
                                                        <Button onClick={this.handleSetPage(page - 1)}>{page}</Button>
                                                        <Button disabled>{page + 1}</Button>
                                                        <Button onClick={this.handleSetPage(page + 1)}>{page + 2}</Button>
                                                        <p>...</p>
                                                        <Button onClick={this.handleSetPage(pages)}>{pages + 1}</Button>
                                                    </>
                                                )
                                    }
                                    <Button disabled={(page + 1) * this.props.pageSize >= this.props.selectedFeatureType.totalWithCriteria} onClick={this.props.nextPage}><div className={'fas fa-angle-right'}/></Button>
                                </>
                            }
                        </span>
                    </div>
                    <div className="header-item buttons-panel" style={{'textAlign': 'right'}}>

                        <span id="ViewSettingsMenuBoundry">
                            <button onClick={() => this.toggleShowViewSettingsMenu()}><span
                                className="fas fa-list"/></button>
                            {this.state.shouldShowViewSettingsMenu &&
                            <ViewSettingsMenu doCloseMenu={this.toggleShowViewSettingsMenu.bind(this)}/>}
                        </span>


                        <span id="toolsMenuBoundry">
                            <button onClick={() => this.toggleShowToolsMenu()}><span
                                className="fas fa-wrench"/></button>
                            {this.state.shouldShowToolsMenu &&
                            <ToolsMenu doCloseMenu={this.toggleShowToolsMenu.bind(this)}/>}
                            <CopyGeometryModal />
                            <CommentsModal />
                        </span>
                    </div>

                </div>


                <div id="df-datagrid"
                     onScroll={() => this.onScroll()}

                     ref={this.addGridRef}
                >

                    <table>
                        <tbody>
                        {features.map((feature, featureIndex) => {

                            const featureDisabled = feature.operation === 'CLOSE' || selectedFeatureType.isLocked || feature.isLocked;
                            const featureHasLocational = !!feature.locational;

                            const rowclasses = classNames({
                                'selected': feature.isSelected,
                                'locked': feature.isLocked || selectedFeatureType.isLocked,
                                'submitted': feature.isExporting,
                                'disabled': featureDisabled
                            });

                            const numberOfNonAttributeCells: number = 4;
                            const positionY = featureIndex;


                            return (
                                <tr className={rowclasses}
                                    key={feature.id}>

                                    <td
                                        onClick={() => this.toggleFeature(feature)}
                                        className="clickable sticky"
                                        key={'title'}
                                    >
                                        <TitleCell feature={feature} comments={this.props.comments[feature.typeId]?.features[feature.id]?.comments ?? []}/>
                                    </td>

                                    {specialAttributes.map((specialAttribute, specialIndex) => {
                                        const hasFocus = gridFocus.x == specialIndex && gridFocus.y == featureIndex;
                                        switch (specialAttribute) {
                                            case 'OPERASJON': {
                                                return (
                                                    <td className="gridcell"
                                                        data-gridcoord={specialIndex + ',' + positionY}
                                                        key={'common' + specialIndex}>
                                                        <OperationsCell hasFocus={hasFocus} feature={feature}/>
                                                    </td>
                                                );
                                            }
                                            case 'VEGSYSTEMREFERANSE': {

                                                const references = feature.roadSystemReferences;
                                                let containsSeveralReferences = false;
                                                let handleShowReferencesModal: () => void | undefined;
                                                let referencesText = '';

                                                if (references) {
                                                    if (references.length > 0) {
                                                        referencesText = references[0];
                                                        if (references.length > 1) {
                                                            containsSeveralReferences = true;
                                                            handleShowReferencesModal = () => this.setState({showMultipleRoadReferences: references});
                                                        }
                                                    }
                                                }



                                                const classes = classNames({
                                                    gridcell: true,
                                                    editable: containsSeveralReferences
                                                });

                                                return (
                                                    <td className={classes}
                                                        data-gridcoord={specialIndex + ',' + positionY}
                                                        key={'common' + specialIndex}>
                                                        <ReadOnlyCell hasFocus={hasFocus} value={referencesText} icon={containsSeveralReferences ? {name: 'glyphicon-plus', title: 'Vis flere referanser', action: handleShowReferencesModal} : undefined}/>

                                                    </td>
                                                );
                                            }
                                            case 'SIDEPOSISJON': {
                                                const classes = classNames({
                                                    gridcell: true,
                                                    editable: featureHasLocational
                                                });
                                                const loading = this.props.loadingSpecialAttributes[feature.id]
                                                    ? this.props.loadingSpecialAttributes[feature.id].SIDEPOSISJON
                                                    : false;

                                                if (!allLocationalsHaveMeteringDirection(feature)) {
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            key={'common' + specialIndex}
                                                            title={'Objektet er stedfestet på veg uten metreringsretning og sideposisjon kan ikke oppgis.'}>
                                                            <ReadOnlyCell hasFocus={hasFocus} value={''}/>
                                                        </td>
                                                    );
                                                }

                                                if (allSidePositionsEqualRelativeToMeteringDirection(feature)) {
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            data-attributetype="sideposition"
                                                            key={'common' + specialIndex}>
                                                            <SidePositionCell loading={loading} hasFocus={hasFocus} feature={feature}/>
                                                        </td>
                                                    );
                                                } else {
                                                    const sidePositions = getAllSidePositionsAsTextRelativeToMeteringDirection(feature);
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            key={'common' + specialIndex}
                                                            title={'Objektet har flere stedfestinger med ulike sideposisjoner som kan redigeres i stedfestingsfanen.'}>
                                                            <ReadOnlyCell hasFocus={hasFocus} value={sidePositions}/>
                                                        </td>
                                                    );
                                                }

                                            }
                                            case 'FELT': {
                                                const classes = classNames({
                                                    gridcell: true,
                                                    editable: featureHasLocational
                                                });

                                                const loading = this.props.loadingSpecialAttributes[feature.id]
                                                    ? this.props.loadingSpecialAttributes[feature.id].FELT
                                                    : false;

                                                if (!allLocationalsHaveMeteringDirection(feature)) {
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            key={'common' + specialIndex}
                                                            title={'Objektet er stedfestet på veg uten metreringsretning og felt kan ikke oppgis.'}>
                                                            <ReadOnlyCell hasFocus={hasFocus} value={''}/>
                                                        </td>
                                                    );
                                                }

                                                if (lanesEqualForAllLocationalsRelativeToMeteringDirection(feature)) {
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            key={'common' + specialIndex}>
                                                            <LaneCell loading={loading} hasFocus={hasFocus} feature={feature}/>
                                                        </td>
                                                    );
                                                } else {
                                                    const lanes = getAllLanesAsTextRelativeToMeteringDirection(feature);
                                                    return (
                                                        <td className={classes}
                                                            data-gridcoord={specialIndex + ',' + positionY}
                                                            key={'common' + specialIndex}
                                                            title={'Objektet har flere stedfestinger med ulike felt som kan redigeres i stedfestingsfanen.'}>
                                                            <ReadOnlyCell hasFocus={hasFocus} value={lanes}/>
                                                        </td>
                                                    );
                                                }

                                            }
                                        }
                                    })}

                                    {qualityParams.map((param, index) => {
                                        const isParamHeightReference =
                                          param.name === "Høydereferanse";
                                        if (isParamHeightReference && !showHeightRef) {
                                            return;
                                        }
                                        const hasFocus = gridFocus.x == numberOfNonAttributeCells + index && gridFocus.y == featureIndex;
                                        const loading = this.props.loadingSpecialAttributes[feature.id]
                                                ? this.props.loadingSpecialAttributes[feature.id].HØYDEREFERANSE
                                                : false;
                                        
                                       const geometryAttributeType = attributeTypes.find((attributetype) => attributetype.valueType === 'SPATIAL' && (
                                            (feature.geometry?.wkt.includes('LINESTRING') && attributetype.name.includes('linje')) ||
                                            (feature.geometry?.wkt.includes('POINT') && attributetype.name.includes('punkt')) ||
                                            (feature.geometry?.wkt.includes('POLYGON') && attributetype.name.includes('flate'))
                                        ));

                                        return (
                                            <td className={`gridcell ${isParamHeightReference ? 'editable' : ''}`}
                                                data-gridcoord={numberOfNonAttributeCells + index + ',' + positionY}
                                                key={param.key}>
                                                {isParamHeightReference ? <HeightReferenceCell feature={feature} hasFocus={hasFocus} loading={loading} geometryAttributeType={geometryAttributeType}/> : <ReadOnlyCell hasFocus={hasFocus}
                                                value={this.getQualityParamValue(feature, param)}/>}
                                            </td>
                                        );
                                    })}

                                    {attributeTypes.map((attributeType, attributeTypeIndex) => {
                                        if (attributeType.valueType === 'SPATIAL') {
                                            return;
                                        }
                                        const attribute = feature.attributes.find(attribute => attribute.typeId === attributeType.id);
                                        const value = attribute ? attribute.value : '';
                                        const positionX = attributeTypeIndex + numberOfNonAttributeCells + qualityParams.length;
                                        const hasFocus = positionX == this.props.gridFocus.x && positionY == this.props.gridFocus.y;

                                        const loading = attribute && loadingAttributes[feature.id]
                                            ? loadingAttributes[feature.id][attribute.typeId]
                                            : false;

                                        return (
                                            <td className={`type-${attributeType.valueType} gridcell editable`}
                                                data-valuetype={attributeType.valueType}
                                                data-attributetype={attributeType.id}
                                                data-value={value}
                                                data-gridcoord={positionX + ',' + positionY}
                                                key={attributeType.id}
                                            ><AttributeCell feature={feature} featureAttributeType={attributeType}
                                                            hasFocus={hasFocus} loading={loading}/></td>
                                        );
                                    })}

                                </tr>
                            );
                        })}
                        </tbody>

                        <thead ref={this.setTableHeadRef}>
                        <tr>
                            <SortingHeader sticky={true}
                                           sortBy={SortBy.NAME}
                                           sortingTitle={'Vegobjekt'}/>
                            <SortingHeader sortBy={SortBy.OPERATION} sortingTitle={'Operasjon'}/>
                            <SortingHeader sortBy={SortBy.ROAD} sortingTitle={'Vegsystemreferanse(r)'} createMeteringDirectionPopOver/>
                            <SortingHeader sortBy={SortBy.SIDE_POSITION} sortingTitle={'Sideposisjon'} createMeteringDirectionPopOver/>
                            <SortingHeader sortBy={SortBy.LANE} sortingTitle={'Felt'} createMeteringDirectionPopOver/>

                            {qualityParams.map(param => {
                                if (param.name === 'Høydereferanse' && !showHeightRef) {
                                    return;
                                }
                                return (
                                    <SortingHeader key={param.key} sortBy={SortBy.QUALITY_PARAMETER}
                                                   qualityParam={param} sortingTitle={param.name} createHeightReferencePopOver={param.name === 'Høydereferanse'}/>
                                );
                            })}

                            {attributeTypes.map(type => {
                                if (type.valueType === 'SPATIAL') {
                                    return;
                                }

                                return (
                                    <SortingHeader
                                        key={type.id}
                                        sortBy={SortBy.ATTRIBUTE}
                                        attributeType={type}
                                        sortingTitle={type.name}
                                        createAttributePopover={true}
                                    />
                                );
                            })}
                        </tr>
                        </thead>
                    </table>

                </div>
            </div>
        );
    }
}


export default connect(mapStateToProps, mapFeaturesTableDispatchToProps)(FeaturesTable);

